import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { Dropdown, useSimpleInputState } from "capitalrx-components-2.0/lib/forms";
import { DataId, Icon, Tooltip, useAlert } from "capitalrx-components-2.0/lib/general";
import { CollapseGroup, LoadingGroup, LoadingSkeleton, Panel } from "capitalrx-components-2.0/lib/general";
import { Grid, Shelf, useResponsive, useResponsiveDisplayMode } from "capitalrx-components-2.0/lib/layout";
import { Stack } from "capitalrx-components-2.0/lib/layout";
import { Body, Heading, Semibold } from "capitalrx-components-2.0/lib/typography";
import { formatDollars } from "capitalrx-utilities";

import { planBenefitCompare, searchTools } from "@/api/axios";
import { PlanCompareResult, SearchTools } from "@/api/types";
import { useNavigateSearch } from "@/premium/hooks";
import { HeadingTile } from "@/premium/pages/ComparePlans/HeadingTile";
import { Breadcrumbs, PageLayout } from "@/premium/themedComponents";
import { useEnrollmentContext } from "@/state";

import "./ComparePlansPage.scss";

const TRANSLATION_PATH = "COMPARE_PLANS_PAGE";

export const ComparePlansPage = () => {
  const { t: translate, i18n } = useTranslation();
  const experienceData = useEnrollmentContext();
  const navigateSearch = useNavigateSearch(false);
  const plans = experienceData.plan_csids;
  const showAlert = useAlert();

  const [expandedIds, onExpandedIdsChange] = useState<ReadonlyArray<string>>(["network", "searchTools", "accumulations"]);
  const [firstPlan, onChangeFirstPlan] = useSimpleInputState<DataId | null>(null);
  const [secondPlan, onChangeSecondPlan] = useSimpleInputState<DataId | null>(null);
  const [thirdPlan, onChangeThirdPlan] = useSimpleInputState<DataId | null>(null);
  const [isLoadingPlanBenefitCompare, setIsLoadingPlanBenefitCompare] = useState([false, false, false]);
  const [planBenefitsCompare, setPlanBenefitsCompare] = useState<Record<string, PlanCompareResult>>(null);
  const [isLoadingSearchTools, setIsLoadingSearchTools] = useState([false, false, false]);
  const [searchToolsCompare, setSearchToolsCompare] = useState<Record<string, SearchTools>>(null);

  const displayMode = useResponsiveDisplayMode();
  const isDesktop = displayMode === "desktop" || displayMode === "smallDesktop";
  const isMobile = displayMode === "mobile";

  const { largeColumns, smallColumns } = useResponsive({
    largeColumns: {
      mobile: 12,
      tablet: 12,
      desktop: 3,
    },
    smallColumns: {
      mobile: 12,
      tablet: 4,
      desktop: 3,
    },
  });

  const mapByCoverageStrategyId = <T extends { coverage_strategy_id: string }>(results: T[]): Record<string, T> => {
    return results.reduce(
      (acc, result) => {
        acc[result.coverage_strategy_id] = result;
        return acc;
      },
      {} as Record<string, T>,
    );
  };

  const isSelectedPlan = (planId: DataId | null): planId is string => planId !== null;
  const planSelections = [firstPlan, secondPlan, thirdPlan] as const;
  const selectedPlans = useMemo(() => planSelections.filter(isSelectedPlan), [firstPlan, secondPlan, thirdPlan]);

  const selectedPlanLoadingStates = useMemo(
    () => [firstPlan !== null, secondPlan !== null, thirdPlan !== null] as const,
    [firstPlan, secondPlan, thirdPlan],
  );

  useEffect(() => {
    if (selectedPlans.length > 0) {
      setIsLoadingPlanBenefitCompare([...selectedPlanLoadingStates]);
      planBenefitCompare({ coverage_strategy_ids: selectedPlans })
        .then((planCompareResult) => {
          setPlanBenefitsCompare(mapByCoverageStrategyId<PlanCompareResult>(planCompareResult.results));
        })
        .catch(() => {
          showAlert({
            message: translate(`${TRANSLATION_PATH}.couldNotFetchPlanData`),
            type: "error",
            style: "banner",
          });
        })
        .finally(() => {
          setIsLoadingPlanBenefitCompare([false, false, false]);
        });
    }
  }, [firstPlan, secondPlan, thirdPlan]);

  useEffect(() => {
    if (selectedPlans.length > 0) {
      setIsLoadingSearchTools([...selectedPlanLoadingStates]);
      searchTools({
        coverage_strategy_ids: selectedPlans,
        client_name: experienceData.experience.identifier,
        language_key: i18n.language,
      })
        .then((searchToolsResult) => {
          setSearchToolsCompare(mapByCoverageStrategyId<SearchTools>(searchToolsResult.results));
        })
        .catch(() => {
          showAlert({
            message: translate(`${TRANSLATION_PATH}.couldNotFetchPlanData`),
            type: "error",
            style: "banner",
          });
        })
        .finally(() => {
          setIsLoadingSearchTools([false, false, false]);
        });
    }
  }, [i18n.language, firstPlan, secondPlan, thirdPlan, experienceData.experience.identifier]);

  const planOptions = useCallback(
    (planDropdown) => {
      // this logic avoids duplicate plan selections in the dropdowns by filtering out the other selected plans
      return plans
        .filter((plan) => {
          if (planDropdown === firstPlan) {
            return plan.coverage_strategy_id !== secondPlan && plan.coverage_strategy_id !== thirdPlan;
          } else if (planDropdown === secondPlan) {
            return plan.coverage_strategy_id !== firstPlan && plan.coverage_strategy_id !== thirdPlan;
          } else if (planDropdown === thirdPlan) {
            return plan.coverage_strategy_id !== firstPlan && plan.coverage_strategy_id !== secondPlan;
          } else {
            return true;
          }
        })
        .map((plan) => (
          <Dropdown.Option key={plan.coverage_strategy_id} value={plan.coverage_strategy_id}>
            {plan.display_name}
          </Dropdown.Option>
        ));
    },
    [plans, firstPlan, secondPlan, thirdPlan],
  );

  const getPlanIdFromCsid = useCallback(
    (csid: DataId | null): number => {
      if (!csid) return null;
      const plan = plans.find((plan) => plan.coverage_strategy_id === (csid as string));
      return plan.id;
    },
    [plans],
  );

  /*
    Returns a translated string for the pharmacy network maintenance type,
    which is either 'open' or 'mail_order_only'. See:
    enrollment.capitalrx.com/backend/capitalrx_enrollment_api/lib/capitalrx_enrollment_api/api/utils/plan_compare.py
  */
  const getNetworkMaintenanceDescription = (maintenance: string) => {
    if (maintenance === "open") {
      return translate(`${TRANSLATION_PATH}.open`);
    } else if (maintenance === "mail_order_only") {
      return translate(`${TRANSLATION_PATH}.mailOrderOnly`);
    } else {
      return maintenance;
    }
  };

  const getNetworkMaintenanceTooltip = (maintenance: string) => {
    if (maintenance === "open") {
      return translate(`${TRANSLATION_PATH}.openMaintenanceDescription`);
    } else if (maintenance === "mail_order_only") {
      return translate(`${TRANSLATION_PATH}.mailOrderOnlyMaintenanceDescription`);
    } else {
      return maintenance;
    }
  };

  /*
    Returns a translated string for the pharmacy network retail type,
    which is either 'broad' or 'narrow'.
  */
  const getNetworkRetailDescription = (retail: string) => {
    if (retail === "broad") {
      return translate(`${TRANSLATION_PATH}.broad`);
    } else if (retail === "narrow") {
      return translate(`${TRANSLATION_PATH}.narrow`);
    } else {
      return retail;
    }
  };

  const getNetworkRetailTooltip = (retail: string) => {
    if (retail === "broad") {
      return translate(`${TRANSLATION_PATH}.broadRetailDescription`);
    } else if (retail === "narrow") {
      return translate(`${TRANSLATION_PATH}.narrowRetailDescription`);
    } else {
      return retail;
    }
  };

  /*
    Returns a translated string for the pharmacy network specialty type,
    which is either 'open' or 'exclusive'.
  */
  const getNetworkSpecialtyDescription = (specialty: string) => {
    if (specialty === "open") {
      return translate(`${TRANSLATION_PATH}.open`);
    } else if (specialty === "exclusive") {
      return translate(`${TRANSLATION_PATH}.exclusive`);
    } else {
      return specialty;
    }
  };

  const getNetworkSpecialtyTooltip = (specialty: string) => {
    if (specialty === "open") {
      return translate(`${TRANSLATION_PATH}.openSpecialtyDescripton`);
    } else if (specialty === "exclusive") {
      return translate(`${TRANSLATION_PATH}.exclusiveSpecialtyDescription`);
    } else {
      return specialty;
    }
  };

  // this was necessary because plan titles are rendered so differently in mobile vs desktop/tablet
  const planTitleSpans = useMemo(() => {
    if (!firstPlan && !secondPlan && !thirdPlan) return [];

    const findPlanName = (planId: DataId | null) => {
      return plans.find((plan) => plan.coverage_strategy_id === planId)?.display_name;
    };

    const spacerSpan = isDesktop ? (
      <Grid.Span columns={smallColumns} key="plan-title-spacer">
        <div />
      </Grid.Span>
    ) : null;

    return [
      spacerSpan,
      ...planSelections.map((planId, index) => (
        <Grid.Span columns={smallColumns} key={`plan-title-${index}`}>
          <Semibold className="plan-semibold">{findPlanName(planId)}</Semibold>
        </Grid.Span>
      )),
    ].filter(Boolean);
  }, [isDesktop, planSelections, plans, smallColumns]);

  const handleChangeFirstPlan = useCallback((newValue: DataId | null, prev: DataId | null) => {
    onChangeFirstPlan(newValue, prev);
  }, []);

  const handleChangeSecondPlan = useCallback((newValue: DataId | null, prev: DataId | null) => {
    onChangeSecondPlan(newValue, prev);
  }, []);

  const handleChangeThirdPlan = useCallback((newValue: DataId | null, prev: DataId | null) => {
    onChangeThirdPlan(newValue, prev);
  }, []);

  const onPlanDetailsClick = (event: React.MouseEvent<HTMLAnchorElement>, planId: number) => {
    if (!planId) return;

    event.preventDefault();
    navigateSearch("/plans/plan-details", {
      enrollmentPlan: planId,
      client: experienceData.experience.identifier,
    });
    requestAnimationFrame(() => {
      window.scrollTo(0, 0);
    });
  };

  const drugPricingIncludedInfo = (hidden: boolean, planId: number) => {
    if (!hidden) {
      return (
        <Stack spacing="small">
          <Semibold className="capitalized-semibold">{translate(`${TRANSLATION_PATH}.included`)}</Semibold>
          <Body>
            <Trans
              i18nKey={`${TRANSLATION_PATH}.includesDrugPricing`}
              components={[
                <a className="link" onClick={(event: React.MouseEvent<HTMLAnchorElement>) => onPlanDetailsClick(event, planId)} />,
              ]}
            />
          </Body>
        </Stack>
      );
    } else {
      return (
        <Stack spacing="small">
          <Semibold className="capitalized-semibold">{translate(`${TRANSLATION_PATH}.excluded`)}</Semibold>
          <Body>{translate(`${TRANSLATION_PATH}.doesNotIncludeDrugPricing`)}</Body>
        </Stack>
      );
    }
  };

  const pharmacySearchIncludedInfo = (hidden: boolean, planId: number) => {
    if (!hidden) {
      return (
        <Stack spacing="small">
          <Semibold className="capitalized-semibold">{translate(`${TRANSLATION_PATH}.included`)}</Semibold>
          <Body>
            <Trans
              i18nKey={`${TRANSLATION_PATH}.includesPharmacySearch`}
              components={[
                <a className="link" onClick={(event: React.MouseEvent<HTMLAnchorElement>) => onPlanDetailsClick(event, planId)} />,
              ]}
            />
          </Body>
        </Stack>
      );
    } else {
      return (
        <Stack spacing="small">
          <Semibold className="capitalized-semibold">{translate(`${TRANSLATION_PATH}.excluded`)}</Semibold>
          <Body>{translate(`${TRANSLATION_PATH}.doesNotIncludePharmacySearch`)}</Body>
        </Stack>
      );
    }
  };

  const formatDollarsOrBlank = (value: number | string | null | undefined): string => {
    if (value == null) {
      return translate(`${TRANSLATION_PATH}.notApplicable`);
    } else {
      return formatDollars(value);
    }
  };

  const shouldShowOnMobile = (hasData: boolean, isLoading: boolean): boolean => {
    return displayMode !== "mobile" || hasData || isLoading;
  };

  const hasNetworkData = (planId: DataId | null): boolean => {
    return !!(planId && planBenefitsCompare?.[planId]?.network);
  };

  const hasSearchToolsData = (planId: DataId | null): boolean => {
    return !!(planId && searchToolsCompare?.[planId]);
  };

  const hasAccumulationsData = (planId: DataId | null): boolean => {
    return !!(planId && planBenefitsCompare?.[planId]?.accumulations);
  };

  const renderMobilePlanTitle = (index: number) => {
    const planId = planSelections[index];
    return isMobile && planId ? planTitleSpans[index] : null;
  };

  const infoTileSkeleton = <LoadingSkeleton style={{ width: "180px", height: "50px" }} count={1} />;

  const renderInfoTile = (content: ReactNode, isLoading: boolean) => {
    return (
      <Grid.Span columns={smallColumns}>
        <div className={displayMode === "mobile" ? "info-tile" : "info-tile info-tile--not-mobile"}>
          <LoadingGroup loading={isLoading}>
            <LoadingGroup.Placeholder fallback={infoTileSkeleton}>{content}</LoadingGroup.Placeholder>
          </LoadingGroup>
        </div>
      </Grid.Span>
    );
  };

  const renderAccumulationsInfo = (
    individualValue: number | string | null | undefined,
    familyValue: number | string | null | undefined,
    individualPlusOneValue?: number | string | null | undefined,
  ) => {
    return (
      <Stack spacing="small">
        <Body>
          <Semibold className="capitalized-semibold">{translate(`${TRANSLATION_PATH}.individual`)}</Semibold>
          {formatDollarsOrBlank(individualValue)}
        </Body>
        {individualPlusOneValue !== undefined && (
          <Body>
            <Semibold className="capitalized-semibold">{translate(`${TRANSLATION_PATH}.individualPlusOne`)}</Semibold>
            {formatDollarsOrBlank(individualPlusOneValue)}
          </Body>
        )}
        <Body>
          <Semibold className="capitalized-semibold">{translate(`${TRANSLATION_PATH}.family`)}</Semibold>
          {formatDollarsOrBlank(familyValue)}
        </Body>
        <Shelf.Spacer />
      </Stack>
    );
  };

  const renderSpecialtyInfo = (planOffset: number) => {
    const plan = planSelections[planOffset];
    return (
      shouldShowOnMobile(hasNetworkData(plan), isLoadingPlanBenefitCompare[planOffset]) && renderInfoTile(
        <Semibold className="capitalized-semibold">
          {getNetworkSpecialtyDescription(planBenefitsCompare?.[plan]?.network?.specialty)}
          {plan && <Tooltip content={getNetworkSpecialtyTooltip(planBenefitsCompare?.[plan]?.network?.specialty)}>
            {(ref) => (
              <span ref={ref}>
                <Icon size="small" name="info-circle" />
              </span>
            )}
          </Tooltip>}
        </Semibold>,
        isLoadingPlanBenefitCompare[planOffset],
      )
    );
  };

  const renderNetworkMaintenanceInfo = (planOffset: number) => {
    const plan = planSelections[planOffset];
    return (
      shouldShowOnMobile(hasNetworkData(plan), isLoadingPlanBenefitCompare[planOffset]) && renderInfoTile(
        <Semibold className="capitalized-semibold">
          {getNetworkMaintenanceDescription(planBenefitsCompare?.[plan]?.network?.maintenance)}
          {plan && <Tooltip content={getNetworkMaintenanceTooltip(planBenefitsCompare?.[plan]?.network?.maintenance)}>
            {(ref) => (
              <span ref={ref}>
                <Icon size="small" name="info-circle" />
              </span>
            )}
          </Tooltip>}
        </Semibold>,
        isLoadingPlanBenefitCompare[planOffset],
      )
    );
  };

  const renderNetworkRetailInfo = (planOffset: number) => {
    const plan = planSelections[planOffset];
    return (
      shouldShowOnMobile(hasNetworkData(plan), isLoadingPlanBenefitCompare[planOffset]) && renderInfoTile(
        <Semibold className="capitalized-semibold">
          {getNetworkRetailDescription(planBenefitsCompare?.[plan]?.network?.retail)}
          {plan && <Tooltip content={getNetworkRetailTooltip(planBenefitsCompare?.[plan]?.network?.retail)}>
            {(ref) => (
              <span ref={ref}>
                <Icon size="small" name="info-circle" />
              </span>
            )}
          </Tooltip>}
        </Semibold>,
        isLoadingPlanBenefitCompare[planOffset],
      )
    );
  };

  return (
    <PageLayout showGradient={false}>
      <Stack spacing="large">
        <Breadcrumbs />
        <Stack spacing="small">
          <Heading>{translate(`${TRANSLATION_PATH}.comparePlans`)}</Heading>
          <Body>{translate(`${TRANSLATION_PATH}.comparePlansDescription`)}</Body>
        </Stack>
        <Stack>
          <div>
            <Grid>
              {isDesktop && (
                <Grid.Span columns={smallColumns}>
                  <div />
                </Grid.Span>
              )}
              <Grid.Span columns={smallColumns}>
                <Dropdown label={translate(`${TRANSLATION_PATH}.planName`)} onChange={handleChangeFirstPlan} value={firstPlan}>
                  {planOptions(firstPlan)}
                </Dropdown>
              </Grid.Span>
              <Grid.Span columns={smallColumns}>
                <Dropdown label={translate(`${TRANSLATION_PATH}.planName`)} onChange={handleChangeSecondPlan} value={secondPlan}>
                  {planOptions(secondPlan)}
                </Dropdown>
              </Grid.Span>
              <Grid.Span columns={smallColumns}>
                <Dropdown
                  disabled={!firstPlan || !secondPlan}
                  label={translate(`${TRANSLATION_PATH}.planName`)}
                  onChange={handleChangeThirdPlan}
                  value={thirdPlan}
                >
                  {planOptions(thirdPlan)}
                </Dropdown>
              </Grid.Span>
            </Grid>
          </div>
          {planSelections.every((item) => item === null) && <Panel>{translate(`${TRANSLATION_PATH}.noPlansSelected`)}</Panel>}
          {planSelections.some((item) => item !== null) && (
            <CollapseGroup className="collapse-group" alignCaret="left" expandedIds={expandedIds} onExpandedIdsChange={onExpandedIdsChange}>
              <CollapseGroup.Collapse id="network" title={<Heading>{translate(`${TRANSLATION_PATH}.network`)}</Heading>}>
                {isDesktop && <Grid className={"plan-title-row"}>{planTitleSpans}</Grid>}
                <Grid className={isMobile ? "info-row-mobile" : "info-row"}>
                  <Grid.Span columns={largeColumns}>
                    <HeadingTile
                      iconName="dollar-circle"
                      title={translate(`${TRANSLATION_PATH}.specialty`)}
                      titleTooltip={translate(`${TRANSLATION_PATH}.specialtyNetworkDescription`)} />
                  </Grid.Span>
                  {displayMode === "tablet" && planTitleSpans}
                  {renderMobilePlanTitle(0)}
                  {renderSpecialtyInfo(0)}
                  {renderMobilePlanTitle(1)}
                  {renderSpecialtyInfo(1)}
                  {renderMobilePlanTitle(2)}
                  {renderSpecialtyInfo(2)}
                </Grid>
                <Grid className={isMobile ? "info-row-mobile" : "info-row"}>
                  <Grid.Span columns={largeColumns}>
                    <HeadingTile
                      iconName="pill-bottle"
                      title={translate(`${TRANSLATION_PATH}.maintenance`)}
                      titleTooltip={translate(`${TRANSLATION_PATH}.maintenanceNetworkDescription`)} />
                  </Grid.Span>
                  {displayMode === "tablet" && planTitleSpans}
                  {renderMobilePlanTitle(0)}
                  {renderNetworkMaintenanceInfo(0)}
                  {renderMobilePlanTitle(1)}
                  {renderNetworkMaintenanceInfo(1)}
                  {renderMobilePlanTitle(2)}
                  {renderNetworkMaintenanceInfo(2)}
                </Grid>
                <Grid className={isMobile ? "info-row-mobile" : "info-row"}>
                  <Grid.Span columns={largeColumns}>
                    <HeadingTile
                      iconName="location"
                      title={translate(`${TRANSLATION_PATH}.retail`)}
                      titleTooltip={translate(`${TRANSLATION_PATH}.retailNetworkDescription`)} />
                  </Grid.Span>
                  {displayMode === "tablet" && planTitleSpans}
                  {renderMobilePlanTitle(0)}
                  {renderNetworkRetailInfo(0)}
                  {renderMobilePlanTitle(1)}
                  {renderNetworkRetailInfo(1)}
                  {renderMobilePlanTitle(2)}
                  {renderNetworkRetailInfo(2)}
                </Grid>
              </CollapseGroup.Collapse>
              <CollapseGroup.Collapse id="searchTools" title={<Heading>{translate(`${TRANSLATION_PATH}.searchTools`)}</Heading>}>
                {isDesktop && <Grid className={"plan-title-row"}>{planTitleSpans}</Grid>}
                <Grid className={isMobile ? "info-row-mobile" : "info-row"}>
                  <Grid.Span columns={largeColumns}>
                    <HeadingTile
                      iconName="search"
                      title={translate(`${TRANSLATION_PATH}.findPharmacies`)}
                      description={translate(`${TRANSLATION_PATH}.findPharmaciesDescription`)}
                    />
                  </Grid.Span>
                  {displayMode === "tablet" && planTitleSpans}
                  {renderMobilePlanTitle(0)}
                  {shouldShowOnMobile(hasSearchToolsData(firstPlan), isLoadingSearchTools[0])
                    && renderInfoTile(
                      searchToolsCompare
                        && firstPlan
                        && drugPricingIncludedInfo(searchToolsCompare?.[firstPlan]?.pharmacy_search_hidden, getPlanIdFromCsid(firstPlan)),
                      isLoadingSearchTools[0],
                    )}
                  {renderMobilePlanTitle(1)}
                  {shouldShowOnMobile(hasSearchToolsData(secondPlan), isLoadingSearchTools[1])
                    && renderInfoTile(
                      searchToolsCompare
                        && secondPlan
                        && drugPricingIncludedInfo(searchToolsCompare?.[secondPlan]?.pharmacy_search_hidden, getPlanIdFromCsid(secondPlan)),
                      isLoadingSearchTools[1],
                    )}
                  {renderMobilePlanTitle(2)}
                  {shouldShowOnMobile(hasSearchToolsData(thirdPlan), isLoadingSearchTools[2])
                    && renderInfoTile(
                      searchToolsCompare
                        && thirdPlan
                        && drugPricingIncludedInfo(searchToolsCompare?.[thirdPlan]?.pharmacy_search_hidden, getPlanIdFromCsid(thirdPlan)),
                      isLoadingSearchTools[2],
                    )}
                </Grid>
                <Grid className={isMobile ? "info-row-mobile" : "info-row"}>
                  <Grid.Span columns={largeColumns}>
                    <HeadingTile
                      iconName="pill-bottle"
                      title={translate(`${TRANSLATION_PATH}.drugPricing`)}
                      description={translate(`${TRANSLATION_PATH}.drugPricingDescription`)}
                    />
                  </Grid.Span>
                  {displayMode === "tablet" && planTitleSpans}
                  {renderMobilePlanTitle(0)}
                  {shouldShowOnMobile(hasSearchToolsData(firstPlan), isLoadingSearchTools[0])
                    && renderInfoTile(
                      searchToolsCompare
                        && firstPlan
                        && pharmacySearchIncludedInfo(searchToolsCompare?.[firstPlan]?.price_search_hidden, getPlanIdFromCsid(firstPlan)),
                      isLoadingSearchTools[0],
                    )}
                  {renderMobilePlanTitle(1)}
                  {shouldShowOnMobile(hasSearchToolsData(secondPlan), isLoadingSearchTools[1])
                    && renderInfoTile(
                      searchToolsCompare
                        && secondPlan
                        && pharmacySearchIncludedInfo(searchToolsCompare?.[secondPlan]?.price_search_hidden, getPlanIdFromCsid(secondPlan)),
                      isLoadingSearchTools[1],
                    )}
                  {renderMobilePlanTitle(2)}
                  {shouldShowOnMobile(hasSearchToolsData(thirdPlan), isLoadingSearchTools[2])
                    && renderInfoTile(
                      searchToolsCompare
                        && thirdPlan
                        && pharmacySearchIncludedInfo(searchToolsCompare?.[thirdPlan]?.price_search_hidden, getPlanIdFromCsid(thirdPlan)),
                      isLoadingSearchTools[2],
                    )}
                </Grid>
              </CollapseGroup.Collapse>
              <CollapseGroup.Collapse id="accumulations" title={<Heading>{translate(`${TRANSLATION_PATH}.accumulations`)}</Heading>}>
                {isDesktop && <Grid className={"plan-title-row"}>{planTitleSpans}</Grid>}
                <Grid className={isMobile ? "info-row-mobile" : "info-row"}>
                  <Grid.Span columns={largeColumns}>
                    <HeadingTile
                      iconName="dollar-circle"
                      title={translate(`${TRANSLATION_PATH}.deductible`)}
                      description={translate(`${TRANSLATION_PATH}.deductibleDescription`)}
                    />
                  </Grid.Span>
                  {displayMode === "tablet" && planTitleSpans}
                  {renderMobilePlanTitle(0)}
                  {shouldShowOnMobile(hasAccumulationsData(firstPlan), isLoadingPlanBenefitCompare[0])
                    && renderInfoTile(
                      planBenefitsCompare?.[firstPlan]?.accumulations
                        && renderAccumulationsInfo(
                          planBenefitsCompare?.[firstPlan]?.accumulations?.individual_deductible,
                          planBenefitsCompare?.[firstPlan]?.accumulations?.family_deductible,
                          planBenefitsCompare?.[firstPlan]?.accumulations?.subscriber_plus_one_family_deductible,
                        ),
                      isLoadingPlanBenefitCompare[0],
                    )}
                  {renderMobilePlanTitle(1)}
                  {shouldShowOnMobile(hasAccumulationsData(secondPlan), isLoadingPlanBenefitCompare[1])
                    && renderInfoTile(
                      planBenefitsCompare?.[secondPlan]?.accumulations
                        && renderAccumulationsInfo(
                          planBenefitsCompare?.[secondPlan]?.accumulations?.individual_deductible,
                          planBenefitsCompare?.[secondPlan]?.accumulations?.family_deductible,
                          planBenefitsCompare?.[secondPlan]?.accumulations?.subscriber_plus_one_family_deductible,
                        ),
                      isLoadingPlanBenefitCompare[1],
                    )}
                  {renderMobilePlanTitle(2)}
                  {shouldShowOnMobile(hasAccumulationsData(thirdPlan), isLoadingPlanBenefitCompare[2])
                    && renderInfoTile(
                      planBenefitsCompare?.[thirdPlan]?.accumulations
                        && renderAccumulationsInfo(
                          planBenefitsCompare?.[thirdPlan]?.accumulations?.individual_deductible,
                          planBenefitsCompare?.[thirdPlan]?.accumulations?.family_deductible,
                          planBenefitsCompare?.[thirdPlan]?.accumulations?.subscriber_plus_one_family_deductible,
                        ),
                      isLoadingPlanBenefitCompare[2],
                    )}
                </Grid>
                <Grid className={isMobile ? "info-row-mobile" : "info-row"}>
                  <Grid.Span columns={largeColumns}>
                    <HeadingTile
                      iconName="shield-check"
                      title={translate(`${TRANSLATION_PATH}.maximumOutOfPocket`)}
                      description={translate(`${TRANSLATION_PATH}.maximumOutOfPocketDescription`)}
                    />
                  </Grid.Span>
                  {displayMode === "tablet" && planTitleSpans}
                  {renderMobilePlanTitle(0)}
                  {shouldShowOnMobile(hasAccumulationsData(firstPlan), isLoadingPlanBenefitCompare[0])
                    && renderInfoTile(
                      planBenefitsCompare?.[firstPlan]?.accumulations
                        && renderAccumulationsInfo(
                          planBenefitsCompare?.[firstPlan]?.accumulations?.individual_out_of_pocket_maximum,
                          planBenefitsCompare?.[firstPlan]?.accumulations?.family_out_of_pocket_maximum,
                        ),
                      isLoadingPlanBenefitCompare[0],
                    )}
                  {renderMobilePlanTitle(1)}
                  {shouldShowOnMobile(hasAccumulationsData(secondPlan), isLoadingPlanBenefitCompare[1])
                    && renderInfoTile(
                      planBenefitsCompare?.[secondPlan]?.accumulations
                        && renderAccumulationsInfo(
                          planBenefitsCompare?.[secondPlan]?.accumulations?.individual_out_of_pocket_maximum,
                          planBenefitsCompare?.[secondPlan]?.accumulations?.family_out_of_pocket_maximum,
                        ),
                      isLoadingPlanBenefitCompare[1],
                    )}
                  {renderMobilePlanTitle(2)}
                  {shouldShowOnMobile(hasAccumulationsData(thirdPlan), isLoadingPlanBenefitCompare[2])
                    && renderInfoTile(
                      planBenefitsCompare?.[thirdPlan]?.accumulations
                        && renderAccumulationsInfo(
                          planBenefitsCompare?.[thirdPlan]?.accumulations?.individual_out_of_pocket_maximum,
                          planBenefitsCompare?.[thirdPlan]?.accumulations?.family_out_of_pocket_maximum,
                        ),
                      isLoadingPlanBenefitCompare[2],
                    )}
                </Grid>
              </CollapseGroup.Collapse>
            </CollapseGroup>
          )}
        </Stack>
      </Stack>
    </PageLayout>
  );
};
